import { useSubscriptionAccessConfigContext } from '@ltvco/refresh-lib/ctx';
import { useSession } from '@ltvco/refresh-lib/v1';
import { UpgradeLimitModal } from '@ltvco/refresh-lib/v2';
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { constants } from 'appConstants';
import { useLimitedPlanInfo } from 'utils/useLimitedPlanInfo';

export function UpgradeLimitModalGate() {
  const isInVariation = useFeatureIsOn('OAR-2027');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isReactivationPath =
    pathname === '/reactivation' || pathname.startsWith('/reactivation/');
  const { session } = useSession();
  const {
    accountLoaded,
    isLoading,
    reportLimit,
    reportsRun,
    reportsRunPercentage,
  } = useLimitedPlanInfo();
  const [upgradeLimitModalShown, setUpgradeLimitModalShown] =
    useState<boolean>(false);
  const hasValidReportLimit = reportLimit > 0 && reportsRun >= 0;
  const remainingReports = hasValidReportLimit ? reportLimit - reportsRun : 0;
  const hasReachedNinetyPercent = reportsRunPercentage >= 90;
  const alertAlreadyShown =
    localStorage.getItem('upgradeLimitModalShown') === 'true';
  const shouldShowAlert =
    accountLoaded &&
    !isLoading &&
    hasValidReportLimit &&
    hasReachedNinetyPercent &&
    !alertAlreadyShown;

  const redirectToUpgradePlan = () => {
    localStorage.setItem('upgradeLimitModalShown', 'true');
    setUpgradeLimitModalShown(false);
    navigate('/upgrade/plan');
  };

  useEffect(() => {
    if (isReactivationPath) {
      setUpgradeLimitModalShown(false);
      return;
    }
    if (shouldShowAlert) {
      setUpgradeLimitModalShown(true);
    } else {
      setUpgradeLimitModalShown(false);
    }
  }, [shouldShowAlert, isReactivationPath]);

  const prevAtNinetyPercentRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (!accountLoaded) {
      return;
    }

    if (!hasValidReportLimit) {
      prevAtNinetyPercentRef.current = null;
      if (localStorage.getItem('upgradeLimitModalShown') === 'true') {
        localStorage.removeItem('upgradeLimitModalShown');
      }
      return;
    }

    const wasAtNinetyPercent = prevAtNinetyPercentRef.current;
    prevAtNinetyPercentRef.current = hasReachedNinetyPercent;

    if (wasAtNinetyPercent === true && !hasReachedNinetyPercent) {
      if (localStorage.getItem('upgradeLimitModalShown') === 'true') {
        localStorage.removeItem('upgradeLimitModalShown');
      }
    }
  }, [accountLoaded, hasValidReportLimit, hasReachedNinetyPercent]);

  const account = session?.account;
  const { vinVariation, fraudScanOnlyVariation } =
    useSubscriptionAccessConfigContext();
  const planUniqueName =
    account?.account?.subscription_info?.subscription_plan_unique_name ?? '';
  const isInternationalSpanishPlan =
    constants?.internationalSpanishPlanNames?.includes(planUniqueName) ?? false;
  const isInternationalFraudscanOnlyPlan =
    constants?.internationalFraudscanOnlyPlanNames?.includes(planUniqueName) ??
    false;
  const hideUpgradeModal =
    vinVariation ||
    isInternationalSpanishPlan ||
    isInternationalFraudscanOnlyPlan ||
    fraudScanOnlyVariation;

  if (
    !isInVariation ||
    hideUpgradeModal ||
    account?.account?.user_info?.user_in_recycling
  ) {
    return null;
  }

  return (
    <UpgradeLimitModal
      firstName={account?.account?.user_info?.first_name ?? ''}
      lastName={account?.account?.user_info?.last_name ?? ''}
      remainingReports={remainingReports}
      isOpen={upgradeLimitModalShown && !isReactivationPath}
      onClose={() => {
        localStorage.setItem('upgradeLimitModalShown', 'true');
        setUpgradeLimitModalShown(false);
      }}
      onClick={redirectToUpgradePlan}
    />
  );
}
